import styled from '@emotion/styled';
import EditorPreviewer from 'components/EditorPreviewer';
import FormField from 'components/FormField';
import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { useFormikContext } from 'formik';
import { useTranslate } from '@tolgee/react';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { Descendant } from 'slate';
import { ScenarioResult } from 'views/Setting/AutoEmit/components/Result';
import {
  gatherI18nLabelMap,
  isMarketingScenarioType,
  isSingleScenarioType,
  scenarioTypeGatherMap,
} from '../config';
import ExecuteContext from './ExecuteContext';
import { PolicyFormValues, Validate } from './share';

export const ResultText = styled.div`
  white-space: pre-wrap;
  > div > div {
    display: block;
    height: auto;
  }
  * {
    text-overflow: unset !important;
    /* overflow: auto !important; */
    vertical-align: middle;
  }
`;

export const Container = styled.div`
  display: flex;
  align-items: flex-start;
  > div {
    flex: 1 0 0;
    max-width: 850px;
  }
  > span {
    flex: 0 0 auto;
    align-self: stretch;
    position: sticky;
    bottom: 80px;
    margin-left: 32px;
    padding-left: 32px;
    border-left: solid 1px var(--fett-border-default);
  }
`;

type FV = Pick<PolicyFormValues, 'bilingualExecuteContext'>;

export interface BilingualExecuteContextFieldProps {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

export const getSplitSegments = (segments: Descendant[]) => {
  return segments
    .reduce<Descendant[][]>(
      (res, cur) => {
        if (cur.type === CustomSlateNodeType.DIVIDING_LINE) {
          res.push([]);

          return res;
        }

        if (cur.type === CustomSlateNodeType.IMAGE || cur.type === CustomSlateNodeType.ITEM) {
          res.push([]);
          res[res.length - 1].push(cur);
          res.push([]);
          return res;
        }

        if (cur.type === CustomSlateNodeType.LOGISTICS) {
          if (cur.preText.trim()) {
            res.push([
              {
                type: 'text',
                text: cur.preText,
              },
            ]);
          }
          res.push([cur]);
          res.push([]);
          return res;
        }

        if (cur.type === CustomSlateNodeType.FILE) {
          if (res[0]) {
            res[0].push(cur);
          } else {
            res.push([cur]);
          }
          return res;
        }

        res[res.length - 1].push(cur);

        return res;
      },
      [[]],
    )
    .filter((v) => getSegmentsLength(v));
};

export const getResultContexts = (
  executeContext: AutoEmit.ExecuteContext<Descendant[]>,
  supportTranslate = false,
) => {
  const { content, translatedContent } = executeContext;

  if (supportTranslate) {
    const transParagraph = translatedContent.filter(
      (v) =>
        v.type === CustomSlateNodeType.PARAGRAPH && (v.children[0] as { text: string }).text !== '',
    );
    const translatedSplitContent = getSplitSegments(translatedContent);

    if (translatedSplitContent.length && transParagraph.length) {
      return translatedSplitContent;
    }
  }

  const splitContent = getSplitSegments(content);

  if (splitContent.length) {
    return splitContent;
  }

  return undefined;
};

export type ContentValidate = (
  ...params: [...Parameters<Validate>, General.PlatformUpperCase]
) => void;

export const validate: ContentValidate = (errors, values, t, platform) => {
  const {
    bilingualExecuteContext: { content, translatedContent },
  } = values;

  const { [platform]: executeContentLimitLength } = platformExecuteContentLimitLengthMap;

  const contentLength = getSegmentsLength(content);
  const translatedContentLength = getSegmentsLength(translatedContent);

  if (contentLength === 0) {
    // eslint-disable-next-line no-param-reassign
    errors.bilingualExecuteContext = t('marketing.请输入下发内容');
  } else if (
    contentLength > executeContentLimitLength ||
    translatedContentLength > executeContentLimitLength
  ) {
    // eslint-disable-next-line no-param-reassign
    errors.bilingualExecuteContext = t('marketing.下发内容不可超出x个字', {
      x: executeContentLimitLength,
    });
  }
};

function BilingualExecuteContextField(props: BilingualExecuteContextFieldProps) {
  const { scenarioType, platform, region } = props;

  const { t } = useTranslate();

  const {
    values: { bilingualExecuteContext },
  } = useFormikContext<FV>();

  const field = (
    <FormField<FV['bilingualExecuteContext']> name="bilingualExecuteContext">
      {({ field: { value, onChange } }) => {
        return (
          <ExecuteContext
            scenarioType={scenarioType}
            platform={platform}
            region={region}
            executeContext={value}
            onExecuteContextChange={onChange}
          />
        );
      }}
    </FormField>
  );

  const resultContext = getResultContexts(bilingualExecuteContext);

  const result = (
    <span>
      <h4>{t('marketing.示例')}</h4>
      <ScenarioResult
        list={[
          ...(() => {
            if (isMarketingScenarioType(scenarioType)) {
              return [
                {
                  type: 'system',
                  content: {
                    ORDER_REMINDER: t('aide.买家咨询商品后，未下单'),
                    PAYMENT_REMINDER: t('aide.买家下单后，未付款'),
                    PRAISE_REMINDER: t('aide.买家签收后/确认收货后'),
                  }[scenarioType],
                } as const,
              ];
            }

            if (isSingleScenarioType(scenarioType)) {
              return [];
            }

            const { [scenarioType]: gather } = scenarioTypeGatherMap;

            if (gather === 'logistics') {
              return [];
            }

            return [
              {
                type: 'system',
                content: t('aide.买家申请x', {
                  x: t(gatherI18nLabelMap[gather]),
                }),
              } as const,
            ];
          })(),
          ...(resultContext
            ? resultContext.map((context) => {
                return {
                  type: 'reply',
                  content: (
                    <ResultText>
                      <EditorPreviewer value={context} />
                    </ResultText>
                  ),
                } as const;
              })
            : [
                {
                  type: 'reply',
                  content: '',
                } as const,
              ]),
        ]}
      />
    </span>
  );

  return (
    <Container>
      {field}
      {result}
    </Container>
  );
}

export default BilingualExecuteContextField;
